import React, { useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { featured } from "../data/featuredProjects";
import styles from "./ProjectDetailsSheet.module.css";
import CustomButton from "../components/CustomButton";
import ScrollStack from "../components/ScrollStack";
import WithSplashOnMediaLoad from "../components/WithSplashOnMediaLoad";
import NotFound from "../pages/NotFound";

const isNonEmpty = (v: unknown) =>
  (typeof v === "string" && v.trim().length > 0) ||
  (Array.isArray(v) && v.length > 0);

const ProjectDetailsSheet: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();                       
  const project = featured.find((p) => p.id === Number(id));
  const [showDescription, setShowDescription] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  if (!project) {
    return <NotFound />;
  }

  const {
    title,
    location,
    description,
    builder,
    leistungen,
    projektzeitraum,
    architekt,
    nutzung,
    bruttoGrundflaeche,
    wohnflaeche,
    gewerbeflaeche,
    grundstuecksflaeche,
    nutzflaeche,
    wohnungsschluessel,
    wohneinheiten,
    anzahlZimmer,
    anzahlStellplaetze,
    besonderheit,
    gewerbeeinheiten,
    credits,
    images,
  } = project;

  const preloadPair = useMemo(() => images.slice(0, 2), [images]);

  const paragraphs = isNonEmpty(description)
    ? String(description)
        .replace(/\r/g, "")
        .split(/\n{2,}/)
        .map((p) => p.replace(/\n+/g, " ").trim())
        .filter(Boolean)
    : [];

  const descriptionMarkup = (
    <div ref={descriptionRef}>
      <section className={styles.descriptionSection}>
        <div className={styles.descriptionInner}>
          <h1 data-desc-title id="project-desc-title">
            {title}
          </h1>
          {isNonEmpty(location) && <h3>{location}</h3>}

          {paragraphs.length > 0 && (
            <div className={styles.projectDescriptionWrapper}>
              <div className={styles.projectDescriptionText}>
                {paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          )}

          <ul className={styles.cardList}>
            {isNonEmpty(builder) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Bauherr / Auftraggeber</div>
                <div className={styles.cardValue}>{builder}</div>
              </li>
            )}

            {isNonEmpty(leistungen) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Leistungen</div>
                <div className={styles.cardValue}>{leistungen}</div>
              </li>
            )}

            {isNonEmpty(projektzeitraum) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Projektzeitraum</div>
                <div className={styles.cardValue}>{projektzeitraum}</div>
              </li>
            )}

            {architekt && typeof architekt === "object" && (
              <>
                {isNonEmpty(architekt.hochbau) && (
                  <li className={styles.cardItem}>
                    <div className={styles.cardLabel}>
                      Architektur (Hochbau)
                    </div>
                    <div className={styles.cardValue}>{architekt.hochbau}</div>
                  </li>
                )}

                {isNonEmpty(architekt.innenausstattung) && (
                  <li className={styles.cardItem}>
                    <div className={styles.cardLabel}>Innenausstattung</div>
                    <div className={styles.cardValue}>
                      {architekt.innenausstattung}
                    </div>
                  </li>
                )}
              </>
            )}

            {typeof architekt === "string" && isNonEmpty(architekt) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Architektur</div>
                <div className={styles.cardValue}>{architekt}</div>
              </li>
            )}

            {isNonEmpty(nutzung) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Nutzung</div>
                <div className={styles.cardValue}>{nutzung}</div>
              </li>
            )}

            {isNonEmpty(wohneinheiten) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Wohneinheiten</div>
                <div className={styles.cardValue}>{wohneinheiten}</div>
              </li>
            )}

            {isNonEmpty(wohnungsschluessel) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Wohnungsschlüssel</div>
                <div className={styles.cardValue}>{wohnungsschluessel}</div>
              </li>
            )}

            {isNonEmpty(wohnflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Wohnfläche</div>
                <div className={styles.cardValue}>{wohnflaeche}</div>
              </li>
            )}

            {isNonEmpty(gewerbeflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Gewerbefläche</div>
                <div className={styles.cardValue}>{gewerbeflaeche}</div>
              </li>
            )}

            {isNonEmpty(nutzflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Nutzfläche</div>
                <div className={styles.cardValue}>{nutzflaeche}</div>
              </li>
            )}

            {isNonEmpty(bruttoGrundflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Bruttogrundfläche (BGF)</div>
                <div className={styles.cardValue}>{bruttoGrundflaeche}</div>
              </li>
            )}

            {isNonEmpty(grundstuecksflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Grundstücksgröße</div>
                <div className={styles.cardValue}>{grundstuecksflaeche}</div>
              </li>
            )}

            {isNonEmpty(anzahlZimmer) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Anzahl Zimmer</div>
                <div className={styles.cardValue}>{anzahlZimmer}</div>
              </li>
            )}

            {isNonEmpty(gewerbeeinheiten) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Gewerbeeinheiten</div>
                <div className={styles.cardValue}>{gewerbeeinheiten}</div>
              </li>
            )}

            {isNonEmpty(anzahlStellplaetze) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Anzahl Stellplätze</div>
                <div className={styles.cardValue}>{anzahlStellplaetze}</div>
              </li>
            )}

            {isNonEmpty(besonderheit) &&
              (Array.isArray(besonderheit) ? (
                <li className={styles.cardItem}>
                  <div className={styles.cardLabel}>Besonderheit</div>
                  <ul className={styles.cardValueList}>
                    {besonderheit.map((s, i) => (
                      <li key={i} className={styles.cardValueItem}>
                        {s}
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li className={styles.cardItem}>
                  <div className={styles.cardLabel}>Besonderheit</div>
                  <div className={styles.cardValue}>{besonderheit}</div>
                </li>
              ))}
          </ul>

          {isNonEmpty(credits) && <p className={styles.credits}>{credits}</p>}

          <div className={styles.backButton}>
    <CustomButton
  text="←  Zurück zu den Referenzen"
  variant="filled"
  size="medium"
  onClick={() => navigate("/projects")}
/>
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <WithSplashOnMediaLoad
      sources={preloadPair}
      delay={700}
      timeout={4000}
      postDelay={600}
    >
      <main className={styles.projectDetails}>
        <ScrollStack
          images={project.images}
          titleAnchorSelector="[data-desc-title]"
          headerOffset={8}
          onScrollEnd={() => setShowDescription(true)}
          onScrollHide={() => setShowDescription(false)}
          credits={project.credits}
        >
          {descriptionMarkup}
        </ScrollStack>

        <section className={styles.footerSpacer} />
      </main>
    </WithSplashOnMediaLoad>
  );
};

export default ProjectDetailsSheet;
